/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useMemo } from "react";
import { PhyInspContainer } from "../platePhysicalInspection.types";
import { useTranslation } from "react-i18next";

interface Props {
	stats: any;
	containers: PhyInspContainer[];
	showConfirmationModal: () => void;
}

const PIFooter = ({ stats, containers, showConfirmationModal }: Props) => {
	const { t } = useTranslation();
	const isRejectionReasonMissing = useMemo(() => {
		return containers.some(
			(c) => c.rejected === true && !(c.rejection_reason ?? "").trim(),
		);
	}, [containers]);
	const isAllUnInspected = useMemo(() => {
		return containers.every((c) => c.rejected === undefined);
	}, [containers]);

	return (
		<div className="fixed bottom-0 left-64 right-0 bg-white px-10 py-6 border-t border-slate-200 flex justify-between items-center shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
			<div className="flex items-center gap-6">
				{!stats.allInspected && (
					<div className="flex items-center gap-3 text-orange-500 bg-orange-50 px-5 py-2.5 rounded-2xl border border-orange-100 animate-pulse">
						<span className="material-symbols-outlined text-xl">
							warning
						</span>
						<span className="text-xs font-black uppercase tracking-[0.15em]">
							{stats.pending}{" "}
							{t(
								"postAutoclaveProcess.physicalInspection.platesPendingInspection",
							)}
						</span>
					</div>
				)}
				{stats.allInspected && isRejectionReasonMissing && (
					<div className="flex items-center gap-3 text-red-500 bg-red-50 px-5 py-2.5 rounded-2xl border border-red-100 animate-pulse">
						<span className="material-symbols-outlined text-xl">
							error
						</span>
						<span className="text-xs font-black uppercase tracking-[0.15em]">
							{t(
								"postAutoclaveProcess.physicalInspection.rejectionReasonsRequired",
							)}
						</span>
					</div>
				)}
				{stats.allInspected && !isRejectionReasonMissing && (
					<div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 px-5 py-2.5 rounded-2xl border border-emerald-100">
						<span className="material-symbols-outlined text-xl">
							verified
						</span>
						<span className="text-xs font-black uppercase tracking-[0.15em]">
							{t(
								"postAutoclaveProcess.physicalInspection.allPlatesInspectedSuccessfully",
							)}
						</span>
					</div>
				)}
			</div>
			<div className="flex gap-4">
				{/* <button className="px-8 py-3.5 border border-slate-200 rounded-2xl text-[11px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 hover:text-slate-800 transition-all">
					{t("postAutoclaveProcess.physicalInspection.back")}
				</button> */}
				<button
					onClick={showConfirmationModal}
					className="px-12 py-3.5 bg-indigo-600 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2.5 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed border-none"
				>
					{t("postAutoclaveProcess.physicalInspection.confirmInspection")}
					<span className="material-symbols-outlined text-lg">
						task_alt
					</span>
				</button>
			</div>
		</div>
	);
};

export default PIFooter;
